import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { BarChart3, FolderKanban, Loader2, RefreshCw } from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import ServiceDeskAPIService, {
  ptsOpenProjectListQueryKey,
  serviceDeskAgentAssignedProjectsQueryKey,
  serviceDeskStatusDashboardQueryKeyWith,
  serviceDeskUserProjectQueryKey,
} from "@sd/services/APIs";
import { projectFilterOptionsWithSelection } from "@sd/lib/ptsDropdownOptions";
import { isExternalSdRole } from "@sd/lib/sdRoles";
import { getServiceDeskUserId, useAuth } from "@sd/hooks/useAuth";
import {
  buildStatusDashboardParams,
  emptyStatusDashboardFilters,
  hasActiveStatusDashboardFilters,
  parseStatusDashboardResponse,
  type StatusDashboardFilters,
} from "@sd/lib/statusDashboardApi";
import {
  TICKET_STATUS_WORKFLOW_ORDER,
  TICKET_STATUS_WORKFLOW_OPTIONS,
  statusColors,
  type TicketStatus,
} from "@sd/constants/ticketStatuses";
import { SearchableMultiFilterSelect } from "@sd/components/common/SearchableMultiFilterSelect";
import { CreatedDateFilterSelect } from "@sd/components/common/CreatedDateFilterSelect";
import {
  buildStatusDashboardDrillDownPath,
  parseStatusDashboardPageSearchParams,
  type StatusDashboardPanel,
} from "@sd/lib/statusDashboardDrillDown";
import { getDefaultCreatedDateRange } from "@sd/lib/ticketCreatedDateFilter";

export function AdminProjectStatsDashboard({
  panel = "admin",
  disableStatusDrillDown = false,
}: {
  panel?: StatusDashboardPanel;
  /** Agent-only: status cards must not navigate to ticket list. Super Agent keeps drill-down. */
  disableStatusDrillDown?: boolean;
}) {
  const navigate = useNavigate();
  const canDrillDownStatuses = !disableStatusDrillDown;
  const [searchParams] = useSearchParams();
  const [projectFilters, setProjectFilters] = useState(
    () =>
      parseStatusDashboardPageSearchParams(searchParams)?.projectFilters ??
      emptyStatusDashboardFilters().projectFilters
  );
  const [createdDateFilter, setCreatedDateFilter] = useState(
    () =>
      parseStatusDashboardPageSearchParams(searchParams)?.createdDateFilter ??
      getDefaultCreatedDateRange()
  );

  useEffect(() => {
    const restored = parseStatusDashboardPageSearchParams(searchParams);
    if (!restored) return;
    setProjectFilters(restored.projectFilters);
    setCreatedDateFilter(restored.createdDateFilter);
  }, [searchParams]);

  const { role } = useAuth();
  const isExternalUser = isExternalSdRole(role);
  const isAgentOnlyRole = role === "ServiceDesk_Agent";
  const scopedUserId = isExternalUser || isAgentOnlyRole ? getServiceDeskUserId() : null;
  const externalUserId = isExternalUser ? scopedUserId : null;
  const agentEmpId = isAgentOnlyRole ? scopedUserId : null;

  const { data: assignedProjectsData, isPending: assignedProjectsLoading, isFetched: assignedProjectsFetched } =
    useQuery({
      queryKey: serviceDeskUserProjectQueryKey(externalUserId),
      queryFn: async () => {
        const response = await ServiceDeskAPIService.userProjects.list(
          externalUserId ?? undefined
        );
        return response.data;
      },
      enabled: Boolean(externalUserId),
      staleTime: 15_000,
    });

  const {
    data: agentAssignedProjectsData,
    isPending: agentAssignedProjectsLoading,
    isFetched: agentAssignedProjectsFetched,
  } = useQuery({
    queryKey: serviceDeskAgentAssignedProjectsQueryKey(agentEmpId),
    queryFn: async () => {
      const response = await ServiceDeskAPIService.agentAssignedProjects.list(
        agentEmpId ?? undefined
      );
      return response.data;
    },
    enabled: Boolean(agentEmpId),
    staleTime: 15_000,
  });

  const { data: ptsProjectsData, isPending: ptsProjectsLoading } = useQuery({
    queryKey: ptsOpenProjectListQueryKey,
    queryFn: async () => {
      const response = await ServiceDeskAPIService.pts.openProjectList();
      return response.data;
    },
    enabled: !isExternalUser && !isAgentOnlyRole,
    staleTime: 5 * 60 * 1000,
  });

  const projectOptions = useMemo(() => {
    const source = isExternalUser
      ? assignedProjectsData
      : isAgentOnlyRole
        ? agentAssignedProjectsData
        : ptsProjectsData;
    return projectFilterOptionsWithSelection(source, projectFilters);
  }, [
    agentAssignedProjectsData,
    assignedProjectsData,
    isAgentOnlyRole,
    isExternalUser,
    projectFilters,
    ptsProjectsData,
  ]);

  const assignedProjectIds = useMemo(
    () =>
      isExternalUser || isAgentOnlyRole
        ? projectOptions.map(option => option.value)
        : null,
    [isAgentOnlyRole, isExternalUser, projectOptions]
  );

  const projectsLoading = isExternalUser
    ? assignedProjectsLoading
    : isAgentOnlyRole
      ? agentAssignedProjectsLoading
      : ptsProjectsLoading;

  const effectiveProjectFilters = useMemo(() => {
    if (!assignedProjectIds) return projectFilters;
    if (projectFilters.length === 0) return assignedProjectIds;
    return projectFilters.filter(id => assignedProjectIds.includes(id));
  }, [assignedProjectIds, projectFilters]);

  const filters = useMemo(
    (): StatusDashboardFilters => ({
      projectFilters: effectiveProjectFilters,
      createdDateFilter,
    }),
    [effectiveProjectFilters, createdDateFilter]
  );

  const dashboardParams = useMemo(
    () => buildStatusDashboardParams(filters),
    [filters]
  );

  const hasActiveFilters = hasActiveStatusDashboardFilters({
    projectFilters,
    createdDateFilter,
  });

  const [openFilterId, setOpenFilterId] = useState<string | null>(null);

  const projectFilterSelectOptions = useMemo(
    () => projectOptions,
    [projectOptions]
  );

  const dashboardReady = isExternalUser
    ? Boolean(externalUserId) && assignedProjectsFetched
    : isAgentOnlyRole
      ? Boolean(agentEmpId) && agentAssignedProjectsFetched
      : true;

  const {
    data: dashboardData,
    isPending: dashboardLoading,
    isError: dashboardError,
    refetch,
    isFetching,
  } = useQuery({
    queryKey: serviceDeskStatusDashboardQueryKeyWith(
      filters.projectFilters,
      filters.createdDateFilter.from,
      filters.createdDateFilter.to
    ),
    queryFn: async () => {
      if (assignedProjectIds && assignedProjectIds.length === 0) {
        return parseStatusDashboardResponse({});
      }
      const response = await ServiceDeskAPIService.dashboard.statusDashboard(
        dashboardParams
      );
      return parseStatusDashboardResponse(response.data);
    },
    enabled: dashboardReady,
    staleTime: 60_000,
    refetchOnMount: "always",
    retry: 1,
  });

  const totalTickets = dashboardData?.total ?? 0;

  const clearFilters = useCallback(() => {
    const defaults = emptyStatusDashboardFilters();
    setProjectFilters(defaults.projectFilters);
    setCreatedDateFilter(defaults.createdDateFilter);
  }, []);

  const openStatusTickets = useCallback(
    (status: TicketStatus) => {
      if (!canDrillDownStatuses) return;
      navigate(
        buildStatusDashboardDrillDownPath(status, filters, panel),
        { replace: false }
      );
    },
    [canDrillDownStatuses, filters, navigate, panel]
  );

  return (
    <div className="min-w-0 space-y-5 p-4 sm:space-y-6 sm:p-6">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
        <div className="min-w-0">
          <div className="mb-1 flex items-center gap-2">
            <BarChart3 className="h-6 w-6 text-primary" />
            <h1 className="text-xl font-bold text-foreground sm:text-2xl">
              Dashboard Project Status
            </h1>
          </div>
        </div>
        <div className="flex w-full shrink-0 flex-col items-stretch gap-2 sm:w-auto sm:flex-row sm:items-center">
          <Badge className="self-end rounded-full border-0 bg-primary/10 px-3 py-1 text-xs font-semibold tabular-nums text-primary sm:self-auto">
            {dashboardLoading || isFetching
              ? "Loading…"
              : dashboardError
                ? "—"
                : `${totalTickets} tickets`}
          </Badge>
          <Button
            type="button"
            variant="outline"
            size="sm"
            className="h-9 shrink-0 gap-1.5"
            onClick={clearFilters}
            disabled={!hasActiveFilters}
            title="Clear all filters"
            aria-label="Clear all filters"
          >
            <RefreshCw className="h-3.5 w-3.5" />
            Clear All Filters
          </Button>
        </div>
      </div>

      <Card className="border-border shadow-sm">
        <CardContent className="flex flex-col gap-3 p-4">
          <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
            Filters
          </p>
          <div className="flex flex-col gap-3 xl:flex-row xl:flex-wrap xl:items-end">
            <div className="w-full min-w-0 sm:max-w-md xl:w-[280px]">
              <label className="mb-1.5 flex items-center gap-1.5 text-xs font-medium text-muted-foreground">
                <FolderKanban className="h-3.5 w-3.5" />
                Project
              </label>
              <SearchableMultiFilterSelect
                selectId="project"
                openSelectId={openFilterId}
                onOpenSelectIdChange={setOpenFilterId}
                values={projectFilters}
                onValuesChange={setProjectFilters}
                options={projectFilterSelectOptions}
                placeholder="All Projects"
                searchPlaceholder="Search projects…"
                disabled={projectsLoading}
                triggerClassName="h-9 w-full"
              />
            </div>

            <div className="w-full min-w-0 sm:w-auto">
              <label className="mb-1.5 block text-xs font-medium text-muted-foreground">
                Created date
              </label>
              <CreatedDateFilterSelect
                value={createdDateFilter}
                onChange={setCreatedDateFilter}
              />
            </div>
          </div>
        </CardContent>
      </Card>

      <Card className="border-primary/20 bg-primary/5">
        <CardContent className="p-4">
          <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
            Total across {TICKET_STATUS_WORKFLOW_ORDER.length} statuses
          </p>
          {dashboardLoading ? (
            <Loader2 className="mt-1 h-6 w-6 animate-spin text-muted-foreground" />
          ) : (
            <p className="text-3xl font-bold tabular-nums text-primary">
              {totalTickets}
            </p>
          )}
        </CardContent>
      </Card>

      {dashboardError ? (
        <div className="flex flex-wrap items-center gap-3 rounded-lg border border-destructive/30 bg-destructive/5 p-4">
          <p className="text-sm text-destructive">
            Status dashboard could not be loaded. Try again or adjust filters.
          </p>
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={() => void refetch()}
          >
            Retry
          </Button>
        </div>
      ) : null}

      <div className="grid auto-rows-fr grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
        {TICKET_STATUS_WORKFLOW_OPTIONS.map(opt => {
          const statusKey = opt.value as TicketStatus;
          const count = dashboardData?.counts[statusKey] ?? 0;
          const accentClass =
            statusColors[statusKey]?.split(" ").find(c => c.startsWith("bg-")) ??
            "bg-muted";

          return (
            <Card
              key={opt.value}
              role={canDrillDownStatuses ? "button" : undefined}
              tabIndex={canDrillDownStatuses ? 0 : undefined}
              title={
                canDrillDownStatuses ? `View ${opt.label} tickets` : undefined
              }
              onClick={() => openStatusTickets(statusKey)}
              onKeyDown={e => {
                if (!canDrillDownStatuses) return;
                if (e.key === "Enter" || e.key === " ") {
                  e.preventDefault();
                  openStatusTickets(statusKey);
                }
              }}
              className={cn(
                "flex h-full flex-col overflow-hidden border border-border bg-card shadow-sm",
                canDrillDownStatuses
                  ? "cursor-pointer transition-shadow hover:border-primary/30 hover:shadow-md focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/40"
                  : "cursor-default"
              )}
            >
              <div className={cn("h-1 w-full shrink-0", accentClass.replace("/30", ""))} />
              <CardContent className="flex flex-1 flex-col p-4">
                <div className="mb-3 flex items-start justify-between gap-2">
                  <p className="min-w-0 flex-1 break-words text-sm font-semibold uppercase leading-snug tracking-wide text-slate-900">
                    {opt.label}
                  </p>
                  <Badge
                    variant="secondary"
                    className={cn(
                      "shrink-0 px-2 py-0.5 text-xs font-bold tabular-nums",
                      statusColors[statusKey]
                    )}
                  >
                    {count}
                  </Badge>
                </div>
                {dashboardLoading ? (
                  <Loader2 className="h-7 w-7 animate-spin text-muted-foreground" />
                ) : dashboardError ? (
                  <p className="text-3xl font-bold text-foreground">—</p>
                ) : (
                  <p className="mt-auto text-3xl font-bold tabular-nums leading-none text-foreground">
                    {count}
                  </p>
                )}
                <p className="mt-2 text-xs font-medium uppercase tracking-wide text-muted-foreground">
                  tickets
                </p>
              </CardContent>
            </Card>
          );
        })}
      </div>
    </div>
  );
}
